// The typed data interfaces of the v2 screens. Steps, sign-offs, rules, what waits on you, templates and script
// import run on the F4 API (./stepsApi.ts, ./script.ts), billing on the F7 API (./billingApi.ts). The gaps that
// have no backend yet (BACKEND-GAPS.md: Explore and Originals, Learn) answer "nothing" (null / empty) for real, and have a mock in ./mock
// (loaded with a dynamic import, only when the mock flag is on). Screens read them through `useSources`.
import { createContext, useContext } from "react";
import type { Api } from "../api.ts";
import type { Department, ExtraDepartment } from "../departments.ts";
import { createBillingSource } from "./billingApi.ts";
import type { ItemAssignment, ItemType } from "./itemCrew.ts";
import { type ScriptImportInfo, scriptImportInfo } from "./script.ts";
import type { DeptSignoff, Person, ServerGate, SignoffRule, StepFacts } from "./steps.ts";
import { type ApiEpisodeSteps, createStepsSource, loadTemplates } from "./stepsApi.ts";

// ── gap 1 (F4): production steps, sign-offs, rules ────────────────────────────────────────────────────────

/** Something that waits on the signed-in person (Home "waiting on you", the mobile sign-off queue). */
export interface WaitingItem {
  id: string;
  dept: Department;
  title: string;
  /** what is up for sign-off: "Script v3", "14 shots" */
  versionLabel: string;
  /** the step's version token as the queue showed it (sent back when signing off); null when unknown */
  version: string | null;
  projectId: string;
  production: string;
  episodeId?: string | null;
  /** "Joe asked for your sign-off" */
  line: string;
  /** the note that came with the request */
  note?: string | null;
  at: string;
  frame?: string | null;
  /** what changed since the last version, if known */
  changed?: string | null;
  /** people who already signed off */
  signedBy?: string[];
  kind: "review" | "read";
}

/** The steps of one episode as the server answers them. */
export interface EpisodeSignoffs {
  signoffs: Partial<Record<Department, DeptSignoff>>;
  /** the server's finals gate (ff.finals_gate); null when the source cannot say */
  gate: ServerGate | null;
  /** departments that lock the finals */
  locks: Department[];
  /** who works on each department (user ids) */
  workers: Partial<Record<Department, string[]>>;
  /** who works on one item of a department (a cast member, set, prop, scene or shot) */
  items: ItemAssignment[];
}

export interface StepsSource {
  /** The state of every department of one episode, and the gate in front of its finals. */
  episode(
    projectId: string,
    episodeId: string,
    facts: StepFacts,
    production: string,
  ): Promise<EpisodeSignoffs>;
  /** The same, from steps the server already answered (GET /production-summaries). */
  episodeFrom(read: ApiEpisodeSteps): Promise<EpisodeSignoffs>;
  /** Who works on each department of a production (ids). */
  assignees(projectId: string): Promise<Partial<Record<Department, string[]>>>;
  /** Null: the backend has no rules (the screen says so). */
  rules(projectId: string, people: readonly Person[]): Promise<SignoffRule[] | null>;
  /** Saves the whole rule; answers what the server stored. */
  saveRule(projectId: string, rule: SignoffRule): Promise<SignoffRule | undefined>;
  /**
   * Adds an extra department to a production (producers and up), or brings a skipped one back; answers its rule.
   * Undefined: this source cannot add steps.
   */
  addStep(projectId: string, dept: ExtraDepartment): Promise<SignoffRule | undefined>;
  /** Sets the steps up as a production template says. */
  applyTemplate(projectId: string, templateId: string): Promise<SignoffRule[]>;
  /**
   * Signs a step off. `version` is the step's version as the person saw it: when the work moved since, the
   * server answers 409 changed_since and records nothing.
   */
  signOff(
    projectId: string,
    episodeId: string,
    dept: Department,
    opts?: { version?: string | null; note?: string | null },
  ): Promise<void>;
  requestChanges(
    projectId: string,
    episodeId: string,
    dept: Department,
    note?: string | null,
    version?: string | null,
  ): Promise<void>;
  /** Puts someone on one item of a step (idempotent); answers the assignment's id. */
  assign(
    projectId: string,
    dept: Department,
    item: { userId: string; itemType: ItemType; itemId: string },
  ): Promise<string>;
  /** Ends an item assignment. */
  unassign(assignmentId: string): Promise<void>;
  /** "Ask for sign-off": the people the step waits on get a notification (their names come back). */
  requestSignoff(
    projectId: string,
    episodeId: string,
    dept: Department,
    note?: string | null,
  ): Promise<{ notified: string[] }>;
  /** What waits on the signed-in person in this workspace. */
  waiting(): Promise<WaitingItem[]>;
}

// ── gap 5: billing ────────────────────────────────────────────────────────────────────────────────────────

/** Where a credit went: a department, or "other" for spend that belongs to none (a cost without a take). */
export type SpendDept = Department | "other";

export interface Balance {
  /** "credits": generations spend from the balance; "unmetered": no balance, usage is only counted */
  billing: "credits" | "unmetered";
  /** the balance in credits (zero and meaningless when unmetered) */
  credits: number;
  /** the balance minus what is still rendering; below `credits` while something renders */
  available: number;
  /** credits used this month */
  usedCredits: number;
  usedByDept: Partial<Record<SpendDept, number>>;
}

/** The plan of a workspace (GET /workspaces/{ws}/plan, F8). `plan` is null and the limits are open when not metered. */
export interface PlanInfo {
  billing: "credits" | "unmetered";
  plan: "free" | "team" | "custom" | null;
  status: "active" | "past_due" | "canceled" | null;
  /** people who hold a seat, and seats paid for (null on Free) */
  seatsUsed: number;
  seatsPaid: number | null;
  /** one seat per month in euros excl. VAT, and the credits that come with it */
  seatEur: number;
  seatCredits: number;
  renewsAt: string | null;
  cancelAtPeriodEnd: boolean;
  /** a failed payment is retried until this moment, then the workspace moves to Free */
  graceUntil: string | null;
  planCreditsPerMonth: number;
  /** the part of the balance that comes from the plan (spent first) */
  planCreditsLeft: number;
  /** unused plan credits roll over up to this many credits */
  rolloverCap: number;
  storageUsedBytes: number;
  /** null: no ceiling */
  storageIncludedBytes: number | null;
  limits: { productions: number | null; productionsUsed: number; final: boolean };
  canBuyPacks: boolean;
  /** the caller is the owner */
  canManage: boolean;
  /** the server has a payment provider; without one the plan is shown and nothing can be bought */
  billingAvailable: boolean;
}

/** A person who holds a seat. */
export interface Seat {
  userId: string;
  name: string;
  role: string;
}

/** The plan with the people who hold its seats (the members with a seat role). */
export interface PlanView {
  plan: PlanInfo;
  seats: Seat[];
}

export interface Pack {
  credits: number;
  priceEur: number;
  /** what the models cost of the price; absent in the mocks (then the margin in force splits it) */
  modelEur?: number;
  marginEur?: number;
}

/** One line of the credit ledger. */
export interface LedgerEntry {
  id: string;
  kind: "grant" | "spend" | "purchase" | "adjustment" | "plan" | "expire" | "storage";
  /** signed: spend is negative */
  credits: number;
  note: string | null;
  at: string;
}

export interface LedgerPage {
  items: LedgerEntry[];
  /** pass back to read the next (older) page; null at the end */
  next: string | null;
}

/** One invoice (GET /workspaces/{ws}/invoices): amounts in euros, VAT apart. */
export interface Invoice {
  id: string;
  number: string | null;
  kind: "plan" | "pack";
  description: string | null;
  amountEurExcl: number;
  vatEur: number;
  /** Stripe's word: paid, open, void, uncollectible */
  status: string;
  pdfUrl: string | null;
  hostedUrl: string | null;
  issuedAt: string;
}

export interface BillingSource {
  balance(ws: string): Promise<Balance | null>;
  /**
   * Null when the server has no plans (before F8): the screen then says nothing about them. `seats: false` skips
   * the list of seat holders (the sidebar only needs the plan).
   */
  plan(ws: string, o?: { seats?: boolean }): Promise<PlanView | null>;
  packs(): Promise<Pack[]>;
  invoices(ws: string): Promise<Invoice[]>;
  /** Stripe Checkout for the Team plan with this many seats (owner only): the address to send the browser to. */
  checkout(ws: string, seats: number): Promise<string>;
  /** Stripe Checkout for a credit pack (owner only). */
  buyPack(ws: string, credits: number): Promise<string>;
  /** More seats (at once, prorated) or fewer (at renewal); answers the plan as it stands. 409 seats_in_use below those held. */
  setSeats(ws: string, seats: number): Promise<PlanInfo>;
  /** The Stripe Customer Portal: card, billing details, VAT number, cancel. */
  portal(ws: string): Promise<string>;
  /** The ledger, newest first. */
  entries(ws: string, cursor?: string | null): Promise<LedgerPage>;
}

// ── gap 3: Explore, Originals, Production Books ───────────────────────────────────────────────────────────

export type FilmFormat = "Series" | "Short film" | "Feature" | "Trailer";

export interface FilmCard {
  id: string;
  title: string;
  by: string;
  remixOf?: string | null;
  format: FilmFormat;
  aspect: string;
  shots?: number | null;
  remixes: number;
  still: string;
  alt: string;
  hasBook: boolean;
  original: boolean;
  following?: boolean;
  /** departments that are open to remix */
  open: Department[];
}

/** A loud moment (APP-SHELL-v2 rule 5): a white card with the spectrum line. At most one per screen. */
export interface Drop {
  eyebrow: string;
  title: string;
  text: string;
  action: string;
  still?: string;
  alt?: string;
}

export interface ExploreQuery {
  tab: "everything" | "originals" | "community" | "following";
  format: FilmFormat | null;
  dept: Department | null;
  sort: "remixed" | "new";
}

export interface BookItem {
  id: string;
  name: string;
  version: string;
  handle?: string;
}

export interface BookSection {
  dept: Department;
  summary: string;
  items: BookItem[];
}

export interface BookVersion {
  label: string;
  note: string;
  still: string;
  alt: string;
  signed: boolean;
}

export interface Book {
  id: string;
  title: string;
  kind: string;
  crew: { role: string; name: string }[];
  meta: string;
  still: string;
  alt: string;
  sections: BookSection[];
  /** the item open in the book */
  item: {
    dept: Department;
    name: string;
    handle: string;
    version: string;
    signedLine: string;
    still: string;
    alt: string;
    usedIn: string;
    versions: BookVersion[];
    prompt: string;
    /** fallback line when `handles` is absent ("uses 3 sets and 1 prop") */
    uses: string;
    /** the @handles in the prompt and the department each belongs to (highlighted in its colour) */
    handles?: { handle: string; dept: Department }[];
    made: { label: string; value: string }[];
    signoffs: { name: string; role: string; signed: boolean; note?: string }[];
    shots: { title: string; still: string; current: boolean }[];
    creditLine: string;
  };
  costByDept: { dept: Department; credits: number }[];
  totalLine: string;
}

export interface ExploreSource {
  featured(): Promise<(FilmCard & { pitch: string }) | null>;
  feed(q: ExploreQuery): Promise<FilmCard[]>;
  drop(): Promise<Drop | null>;
  book(id: string): Promise<Book | null>;
}

// ── gap 4: Learn ──────────────────────────────────────────────────────────────────────────────────────────

export interface Lesson {
  n: number;
  dept: Department;
  title: string;
  outcome: string;
  minutes?: number;
  done: boolean;
}

export interface Course {
  title: string;
  eyebrow: string;
  intro: string;
  meta: string;
  still: string;
  alt: string;
  stillNote: string;
  lessons: Lesson[];
}

export interface LearnSource {
  course(): Promise<Course | null>;
  live(): Promise<Drop | null>;
}

// ── gaps 6, 7 (F4): starting a production ─────────────────────────────────────────────────────────────────

export interface Template {
  /** the server's template id (episode, short, feature, trailer) */
  id: string;
  name: string;
  type: string;
  aspect: string;
  /** one sentence for the card */
  blurb?: string;
  /** departments the template skips */
  skipped?: Department[];
  /** a still for the card, when there is one (the mocks); the real templates have none */
  still?: string | null;
}

export interface StartSource {
  /** an Original to remix from (Home first run, New production); no backend yet (gap 3) */
  original(): Promise<FilmCard | null>;
  templates(): Promise<Template[]>;
  /** null: this server cannot import scripts from the app */
  scriptImport(): Promise<ScriptImportInfo | null>;
}

// ── all of them ───────────────────────────────────────────────────────────────────────────────────────────

export interface Sources {
  /** the mock flag is on and the mocks are loaded */
  mock: boolean;
  steps: StepsSource;
  billing: BillingSource;
  explore: ExploreSource;
  learn: LearnSource;
  start: StartSource;
}

const none = async () => null;
const empty = async () => [];
const noBilling = async (): Promise<never> => {
  throw new Error("Billing is not available here.");
};

const NO_STEPS: StepsSource = {
  episode: async () => ({ signoffs: {}, gate: null, locks: [], workers: {}, items: [] }),
  episodeFrom: async () => ({ signoffs: {}, gate: null, locks: [], workers: {}, items: [] }),
  assignees: async () => ({}),
  rules: none,
  saveRule: async () => undefined,
  addStep: async () => undefined,
  applyTemplate: empty,
  signOff: async () => undefined,
  requestChanges: async () => undefined,
  assign: async () => "",
  unassign: async () => undefined,
  requestSignoff: async () => ({ notified: [] }),
  waiting: empty,
};

/** Sources that answer "nothing": the context's default, before the provider knows the API and workspace. */
export const NO_SOURCES: Sources = {
  mock: false,
  steps: NO_STEPS,
  billing: {
    balance: none,
    plan: none,
    packs: empty,
    invoices: empty,
    checkout: noBilling,
    buyPack: noBilling,
    setSeats: noBilling,
    portal: noBilling,
    entries: async () => ({ items: [], next: null }),
  },
  explore: { featured: none, feed: empty, drop: none, book: none },
  learn: { course: none, live: none },
  start: { original: none, templates: empty, scriptImport: none },
};

/** The real sources: steps, templates, script import and billing on the API; the rest has no backend yet. */
export function createRealSources(api: Api, ws: string | null, meId: string | null | undefined): Sources {
  if (!ws) return NO_SOURCES;
  let info: Promise<ScriptImportInfo | null> | null = null;
  return {
    ...NO_SOURCES,
    steps: createStepsSource(api, ws, meId),
    billing: createBillingSource(api),
    start: {
      original: none,
      templates: () => loadTemplates(api, ws),
      scriptImport: () => {
        info ??= scriptImportInfo(api, ws);
        return info;
      },
    },
  };
}

export const SourcesContext = createContext<Sources>(NO_SOURCES);
export const useSources = (): Sources => useContext(SourcesContext);
